import { onMounted, onUnmounted, type Ref } from 'vue'

import { prefersReducedMotion } from '@/utils/onEnter'

/**
 * 3D-параллакс hero-сцены: при движении курсора над сценой вращает внутренний
 * слой до ±14° и сдвигает карточки по Z согласно их data-depth. Отключён при
 * reduced-motion. Сами idle-анимации карточек гасит флаг data-anim в CSS.
 *
 * @param scene - ref на контейнер `.scene` (область наведения)
 * @param inner - ref на `.scene-inner` (вращаемый слой с карточками)
 */
export function useSceneParallax(scene: Ref<HTMLElement | null>, inner: Ref<HTMLElement | null>): void {
  let raf = 0

  const onMove = (event: MouseEvent): void => {
    const sceneEl = scene.value
    const innerEl = inner.value
    if (sceneEl === null || innerEl === null) return

    const rect = sceneEl.getBoundingClientRect()
    const px = (event.clientX - rect.left) / rect.width - 0.5
    const py = (event.clientY - rect.top) / rect.height - 0.5

    cancelAnimationFrame(raf)
    raf = requestAnimationFrame(() => {
      innerEl.style.transform = `rotateY(${px * 14}deg) rotateX(${-py * 14}deg)`
      innerEl.querySelectorAll<HTMLElement>('.float-card').forEach((card) => {
        const depth = Number.parseFloat(card.dataset.depth ?? '20')
        card.style.transform = `translateZ(${depth}px) translate(${px * depth * 0.5}px, ${py * depth * 0.5}px)`
      })
    })
  }

  const reset = (): void => {
    if (inner.value !== null) inner.value.style.transform = ''
  }

  onMounted(() => {
    const sceneEl = scene.value
    if (sceneEl === null || prefersReducedMotion()) return
    sceneEl.addEventListener('mousemove', onMove)
    sceneEl.addEventListener('mouseleave', reset)
  })

  onUnmounted(() => {
    const sceneEl = scene.value
    sceneEl?.removeEventListener('mousemove', onMove)
    sceneEl?.removeEventListener('mouseleave', reset)
    cancelAnimationFrame(raf)
  })
}
